/*
    This is the form controller. It handles the data manipulation
    and submission for creating a Job Day.
*/
import React, { useState, useCallback } from "react";
import moment from "moment";

// components
import SideViewModal from "../../../../../components/modals/SideViewModal/SideViewModal.component";

// collections
import AddJobDay from "../../AddJobDay.form";

// apollo
import { useMutation } from "@apollo/client";
import { GET_ALL_JOB_DAYS_IN_DATE_RANGE } from "../../../../../graphql/jobDay/jobDay.queries";
import { CREATE_JOB_DAY } from "../../../../../graphql/jobDay/jobDay.mutations";

// utils
import { validate } from "../../../../../shared/utility";

// --- helpers ---
function toInt(v) {
  const n = Number.parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
}

function extractApolloError(err) {
  const msgs = [];
  if (err?.graphQLErrors?.length) {
    for (const ge of err.graphQLErrors) {
      if (ge?.message) msgs.push(ge.message);
      const det = ge?.extensions?.exception?.detail || ge?.extensions?.detail;
      if (det) msgs.push(det);
      const fieldErrors = ge?.extensions?.exception?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        for (const [f, arr] of Object.entries(fieldErrors)) {
          msgs.push(
            `${f}: ${Array.isArray(arr) ? arr.join(", ") : String(arr)}`
          );
        }
      }
    }
  }
  if (err?.networkError?.result?.errors?.length) {
    for (const e of err.networkError.result.errors)
      if (e?.message) msgs.push(e.message);
  }
  if (!msgs.length && err?.networkError?.message)
    msgs.push(err.networkError.message);
  if (!msgs.length && err?.message) msgs.push(err.message);
  return msgs.join("\n");
}

export default function CreateJobDay({
  visible,
  closeForm,
  rangeStart,
  rangeEnd,
}) {
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [createJobDay] = useMutation(CREATE_JOB_DAY, {
    awaitRefetchQueries: true,
  });

  const handleSubmit = useCallback(
    (formData) => {
      if (isSaving) return;

      const { isValid, errors: vErrors } = validate(formData, {
        job: { type: "input", validation: { required: true } },
        scopeOfWork: { type: "input", validation: {} },
        date: { type: "date", validation: { required: true, isDate: true } },
        startTime: {
          type: "time",
          validation: { required: true, isTime: true },
        },
        endTime: { type: "time", validation: { isTime: true } },
        vehicleOption: { type: "input", validation: {} }, // ADD VALIDATION (optional field)
      });

      if (!isValid) {
        setErrors(vErrors);
        return;
      }

      const crewAssignments = Array.isArray(formData.crewAssignments)
        ? formData.crewAssignments
            .map((a) => toInt(a?.id))
            .filter(Number.isInteger)
        : [];

      const dateStr = moment(formData.date).isValid()
        ? moment(formData.date).format("YYYY-MM-DD")
        : null;

      const startTime = moment(formData.startTime).isValid()
        ? moment(formData.startTime).format("HH:mm:ss")
        : null;

      const endTime =
        formData.endTime && moment(formData.endTime).isValid()
          ? moment(formData.endTime).format("HH:mm:ss")
          : null;

      // Handle vehicleOption - send null if empty string
      const vehicleOption = formData.vehicleOption || null;

      const variables = {
        job: toInt(formData.job),
        truck: toInt(formData.truck),
        driver: toInt(formData.driver),
        truck2: toInt(formData.truck2),
        driver2: toInt(formData.driver2),
        truck3: toInt(formData.truck3),
        driver3: toInt(formData.driver3),
        scopeOfWork: formData.scopeOfWork,
        crewAssignments,
        date: dateStr,
        startTime,
        endTime,
        vehicleOption, // ADD vehicleOption TO VARIABLES
      };

      // Compute the range for the refetch
      const baseDay =
        dateStr && moment(dateStr).isValid() ? moment(dateStr) : moment();
      const startDate = (
        rangeStart && moment(rangeStart).isValid()
          ? moment(rangeStart)
          : baseDay.clone().startOf("month")
      ).format("YYYY-MM-DD");
      const endDate = (
        rangeEnd && moment(rangeEnd).isValid()
          ? moment(rangeEnd)
          : baseDay.clone().endOf("month")
      ).format("YYYY-MM-DD");

      setIsSaving(true);
      setErrors({});

      createJobDay({
        variables,
        refetchQueries: [
          {
            query: GET_ALL_JOB_DAYS_IN_DATE_RANGE,
            variables: { startDate, endDate },
          },
        ],
      })
        .then((result) => {
          if (!result?.data) {
            setErrors({
              __server:
                "Create failed. Please verify your inputs and try again.",
            });
            return;
          }
          setErrors({});
          closeForm();
        })
        .catch((e) => {
          const msg = extractApolloError(e) || "Something went wrong.";
          setErrors({ __server: msg });
          console.error(e);
        })
        .finally(() => setIsSaving(false));
    },
    [createJobDay, isSaving, closeForm, rangeStart, rangeEnd]
  );

  if (!visible) return null;

  const addVariants = [
    {
      name: "Add Job Day",
      component: (
        <AddJobDay errors={errors} status={isSaving ? "saving" : "idle"} />
      ),
      stages: 1,
    },
  ];

  return (
    <SideViewModal
      show={visible}
      submitText={isSaving ? "Saving…" : "Add Job Day"}
      submitDisabled={isSaving}
      isSubmitting={isSaving}
      onClose={() => {
        setErrors({});
        if (!isSaving) closeForm();
      }}
      onSubmit={handleSubmit}
      title="New Job Day"
      variants={addVariants}
      errors={errors}
    />
  );
}